import { P2GButton, P2GText } from '@parcel2go/core-ui';
import { P2GGrid } from '@parcel2go/core-ui';
import { P2GBox } from '@parcel2go/core-ui';
import { QuoteOptionApiModel } from '~/lib/api/smartsend/types';
import { toDisplayCurrency } from '~/components/currency/toDisplayCurrency';
import { TranslationNamespaces } from '~/lib/constants';
import { Trans, useTranslation } from 'react-i18next';
import { useTaxName } from '@parcel2go/npm-p2g-app';
import { QuoteOptionLabel } from './QuoteOptionLabel';

interface props {
  estimatedValue: number;
  isSelected: boolean;
  onSelect: () => void;
  option: QuoteOptionApiModel;
  currencyCode: string;
  isXs: boolean;
}

export function QuoteOptionButton(props: props) {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const taxName = useTaxName();
  const isMain = props.option.CoverPercentage === 100;

  if (props.option.Unavailable) {
    return (
      <P2GGrid container alignItems="center" wrap="nowrap" gap={1}>
        <P2GGrid item flexGrow={1}>
          <P2GBox className="smartsend-quote-option-price">
            <P2GText style={{ fontWeight: isMain ? 'bold' : 'normal' }} variant="body2">
              <Trans t={t} i18nKey="QUOTE_OPTION_UNAVAILABLE_MESSAGE" defaults="Unavailable<br />on this service" />
            </P2GText>
          </P2GBox>
        </P2GGrid>
        <P2GGrid item flexShrink={1} xs={12} sm="auto">
          <div style={{ opacity: 0.5 }}>
            <P2GButton disabled={true} sx={{ minWidth: '205px' }} fullWidth={props.isXs}>
              <QuoteOptionLabel
                option={props.option}
                estimatedValue={props.estimatedValue}
                currencyCode={props.currencyCode}
              />
            </P2GButton>
          </div>
        </P2GGrid>
      </P2GGrid>
    );
  }

  return (
    <P2GBox>
      <P2GGrid container alignItems="center" wrap={props.isXs ? 'wrap' : 'nowrap'} direction="row" spacing={2}>
        <P2GGrid item flexGrow={1} xs={12} sm="auto">
          <P2GBox
            className="smartsend-quote-option-price"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
              justifyContent: 'space-between'
            }}
          >
            <div>
              {props.option.TotalVat > 0 ? (
                <>
                  <div>
                    <P2GText component="span" variant="h4" value={props.option.TotalPriceExVat}>
                      {toDisplayCurrency(props.currencyCode, props.option.TotalPriceExVat)}
                    </P2GText>{' '}
                    <P2GText component="span" variant="body2">
                      <span style={{ fontSize: '10px' }}>
                        {t(`EXCLUDING_TAX`, { defaultValue: `exc {{taxName}}`, taxName: taxName })}
                      </span>
                    </P2GText>
                  </div>
                  <div>
                    <P2GText component="span" variant="body2" value={props.option.TotalPrice}>
                      {toDisplayCurrency(props.currencyCode, props.option.TotalPrice)}
                    </P2GText>{' '}
                    <P2GText component="span" variant="body2">
                      <span style={{ fontSize: '10px' }}>
                        {t(`INCLUDING_TAX`, { defaultValue: `inc {{taxName}}`, taxName: taxName })}
                      </span>
                    </P2GText>
                  </div>
                </>
              ) : (
                <div>
                  <P2GText component="span" variant="h4" value={props.option.TotalPrice}>
                    {toDisplayCurrency(props.currencyCode, props.option.TotalPrice)}
                  </P2GText>{' '}
                  <P2GText component="span" variant="body2">
                    <span style={{ fontSize: '10px' }}>
                      {t(`INCLUDING_TAX`, { defaultValue: `inc {{taxName}}`, taxName: taxName })}
                    </span>
                  </P2GText>
                </div>
              )}
            </div>
            <P2GText style={{ fontWeight: isMain ? 'bold' : 'normal' }} variant="body2">
              <QuoteOptionLabel
                option={props.option}
                estimatedValue={props.estimatedValue}
                currencyCode={props.currencyCode}
              />
            </P2GText>
          </P2GBox>
        </P2GGrid>
        <P2GGrid item flexShrink={1} xs={12} sm="auto">
          <P2GButton
            variant={props.isSelected ? 'tertiary' : isMain ? 'primary' : 'secondary'}
            onClick={props.onSelect}
            sx={{ minWidth: '205px' }}
            fullWidth={props.isXs}
          >
            {props.isSelected
              ? t('SERVICE_SELECTED_BUTTON', { defaultValue: 'Selected' })
              : t('SERVICE_SELECT_BUTTON', { defaultValue: 'Select Service' })}
          </P2GButton>
        </P2GGrid>
      </P2GGrid>
    </P2GBox>
  );
}
